/**
 * CalendarSyncButton Component
 * Reusable button for importing/sharing calendars across all dashboards
 */

import React, { useState } from 'react';
import { Calendar, Link, Unlink, RefreshCw, Check, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/contexts/LanguageContext';
import { CalendarProvider } from '@/types/calendar-integration.types';

interface ConnectedCalendar {
  provider: CalendarProvider;
  email: string;
  syncEnabled: boolean;
  lastSync?: Date;
}

interface CalendarSyncButtonProps {
  onSync?: () => void;
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'sm' | 'default' | 'lg';
  showLabel?: boolean;
}

const translations = {
  en: {
    syncCalendar: 'Sync Calendar',
    connectCalendar: 'Connect Calendar',
    connectedCalendars: 'Connected Calendars',
    noConnections: 'No calendars connected',
    connectNew: 'Connect new calendar',
    google: 'Google Calendar',
    outlook: 'Outlook Calendar',
    calendly: 'Calendly',
    disconnect: 'Disconnect',
    syncNow: 'Sync Now',
    autoSync: 'Auto-sync enabled',
    lastSync: 'Last sync',
    syncSettings: 'Sync Settings',
    bidirectional: 'Bidirectional sync',
    bidirectionalDesc: 'Changes sync both ways between platforms',
    notifications: 'Sync notifications',
    notificationsDesc: 'Get notified when events are synced',
    connecting: 'Connecting...',
    connected: 'Connected',
    error: 'Connection failed',
    syncSuccess: 'Calendar synced successfully',
    syncError: 'Failed to sync calendar',
    disconnectConfirm: 'Are you sure you want to disconnect this calendar?',
    cancel: 'Cancel',
    confirm: 'Confirm',
    shareCalendar: 'Share My Calendar',
    importCalendar: 'Import Calendar',
    manageConnections: 'Manage Connections',
  },
  fr: {
    syncCalendar: 'Synchroniser le calendrier',
    connectCalendar: 'Connecter un calendrier',
    connectedCalendars: 'Calendriers connectés',
    noConnections: 'Aucun calendrier connecté',
    connectNew: 'Connecter un nouveau calendrier',
    google: 'Google Agenda',
    outlook: 'Outlook Calendrier',
    calendly: 'Calendly',
    disconnect: 'Déconnecter',
    syncNow: 'Synchroniser maintenant',
    autoSync: 'Synchronisation automatique activée',
    lastSync: 'Dernière synchronisation',
    syncSettings: 'Paramètres de synchronisation',
    bidirectional: 'Synchronisation bidirectionnelle',
    bidirectionalDesc: 'Les modifications se synchronisent dans les deux sens',
    notifications: 'Notifications de synchronisation',
    notificationsDesc: 'Être notifié lorsque les événements sont synchronisés',
    connecting: 'Connexion...',
    connected: 'Connecté',
    error: 'Échec de connexion',
    syncSuccess: 'Calendrier synchronisé avec succès',
    syncError: 'Échec de la synchronisation du calendrier',
    disconnectConfirm: 'Êtes-vous sûr de vouloir déconnecter ce calendrier?',
    cancel: 'Annuler',
    confirm: 'Confirmer',
    shareCalendar: 'Partager mon calendrier',
    importCalendar: 'Importer un calendrier',
    manageConnections: 'Gérer les connexions',
  }
};

export function CalendarSyncButton({ 
  onSync, 
  variant = 'outline', 
  size = 'default',
  showLabel = true 
}: CalendarSyncButtonProps) {
  const { toast } = useToast();
  const { language } = useLanguage();
  const t = translations[language];
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectedCalendars, setConnectedCalendars] = useState<ConnectedCalendar[]>([]);
  const [syncSettings, setSyncSettings] = useState({
    bidirectional: true,
    notifications: true
  });

  // Simulated OAuth flow - in production, this would use real OAuth
  const handleConnect = async (provider: CalendarProvider) => {
    setIsConnecting(true);
    
    try {
      // Simulate OAuth popup
      const width = 500;
      const height = 600;
      const left = (window.innerWidth - width) / 2;
      const top = (window.innerHeight - height) / 2;
      
      // In production, this would open actual OAuth URL
      const authUrl = getAuthUrl(provider);
      
      // For demo, simulate successful connection after delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const newCalendar: ConnectedCalendar = {
        provider,
        email: provider === 'google' ? 'user@gmail.com' : 'user@outlook.com',
        syncEnabled: true,
        lastSync: new Date()
      };
      
      setConnectedCalendars(prev => [...prev, newCalendar]);
      
      toast({
        title: t.connected,
        description: `${getProviderName(provider)} ${t.connected.toLowerCase()}`,
      });
    } catch (error) {
      toast({
        title: t.error,
        description: String(error),
        variant: 'destructive'
      });
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = (provider: CalendarProvider) => {
    setConnectedCalendars(prev => prev.filter(c => c.provider !== provider));
    toast({
      title: t.disconnect,
      description: `${getProviderName(provider)} disconnected`,
    });
  };

  const handleSync = async () => {
    try {
      // Trigger sync for all connected calendars
      await Promise.all(connectedCalendars.map(async (cal) => {
        // Simulate sync
        await new Promise(resolve => setTimeout(resolve, 1000));
      }));
      
      setConnectedCalendars(prev => 
        prev.map(cal => ({ ...cal, lastSync: new Date() }))
      );
      
      toast({
        title: t.syncSuccess,
      });
      
      onSync?.();
    } catch (error) {
      toast({
        title: t.syncError,
        variant: 'destructive'
      });
    }
  };

  const getAuthUrl = (provider: CalendarProvider): string => {
    // In production, these would be actual OAuth URLs
    const baseUrl = window.location.origin;
    const redirectUri = `${baseUrl}/auth/callback/${provider}`;
    
    if (provider === 'google') {
      return `https://accounts.google.com/o/oauth2/v2/auth?client_id=YOUR_CLIENT_ID&redirect_uri=${redirectUri}&response_type=code&scope=https://www.googleapis.com/auth/calendar`;
    }
    if (provider === 'outlook') {
      return `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=YOUR_CLIENT_ID&redirect_uri=${redirectUri}&response_type=code&scope=Calendars.ReadWrite`;
    }
    return '';
  };

  const getProviderName = (provider: CalendarProvider): string => {
    switch (provider) {
      case 'google': return t.google;
      case 'outlook': return t.outlook;
      case 'calendly': return t.calendly;
      default: return provider;
    }
  };

  const getProviderIcon = (provider: CalendarProvider) => {
    switch (provider) {
      case 'google':
        return (
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
        );
      case 'outlook':
        return (
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#0078D4" d="M24 7.387v10.478c0 .23-.08.424-.238.576a.806.806 0 0 1-.583.234h-8.207v-6.348l1.602 1.157a.37.37 0 0 0 .447-.008l6.952-5.09v-.999h-.016a.346.346 0 0 0-.148-.076.45.45 0 0 0-.156-.028H13.94L12.16 8.93v-.018l-.012.008-.006-.008v.018l-1.78-1.647H.37a.45.45 0 0 0-.157.028.346.346 0 0 0-.147.076H.05v10.478c0 .23.08.424.238.576a.806.806 0 0 0 .583.234h8.207v-6.348l-1.602 1.157a.37.37 0 0 1-.447-.008L.077 8.386v-.999h.016A.346.346 0 0 1 .24 7.31a.45.45 0 0 1 .156-.028h9.685L12 8.93v.018l.012-.008.006.008v-.018l1.78 1.647h9.685a.45.45 0 0 1 .156.028c.059.024.11.048.148.076h.016v.714z"/>
            <path fill="#0078D4" d="M7.88 12.797a4.5 4.5 0 1 0-4.5 4.5 4.5 4.5 0 0 0 4.5-4.5zm-7.5 0a3 3 0 1 1 3 3 3 3 0 0 1-3-3z"/>
          </svg>
        );
      default:
        return <Calendar className="w-5 h-5" />;
    }
  };

  const formatLastSync = (date?: Date) => {
    if (!date) return 'Never';
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant={variant} size={size} className="gap-2">
            <Calendar className="h-4 w-4" />
            {showLabel && t.syncCalendar}
            {connectedCalendars.length > 0 && (
              <Badge variant="secondary" className="ml-1 px-1.5 py-0.5 text-xs">
                {connectedCalendars.length}
              </Badge>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem onClick={() => handleConnect('google')} disabled={isConnecting}>
            {getProviderIcon('google')}
            <span className="ml-2">{t.google}</span>
            {connectedCalendars.some(c => c.provider === 'google') && (
              <Check className="ml-auto h-4 w-4 text-green-500" />
            )}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleConnect('outlook')} disabled={isConnecting}>
            {getProviderIcon('outlook')}
            <span className="ml-2">{t.outlook}</span>
            {connectedCalendars.some(c => c.provider === 'outlook') && (
              <Check className="ml-auto h-4 w-4 text-green-500" />
            )}
          </DropdownMenuItem>
          
          {connectedCalendars.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleSync}>
                <RefreshCw className="h-4 w-4 mr-2" />
                {t.syncNow}
              </DropdownMenuItem>
              <DialogTrigger asChild>
                <DropdownMenuItem>
                  <Link className="h-4 w-4 mr-2" />
                  {t.manageConnections}
                </DropdownMenuItem>
              </DialogTrigger>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t.connectedCalendars}</DialogTitle>
          <DialogDescription>{t.syncSettings}</DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Connected Calendars List */}
          {connectedCalendars.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              <Calendar className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>{t.noConnections}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {connectedCalendars.map((cal) => (
                <div 
                  key={cal.provider}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    {getProviderIcon(cal.provider)}
                    <div>
                      <p className="font-medium">{getProviderName(cal.provider)}</p>
                      <p className="text-xs text-muted-foreground">{cal.email}</p>
                      <p className="text-xs text-muted-foreground">
                        {t.lastSync}: {formatLastSync(cal.lastSync)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch 
                      checked={cal.syncEnabled}
                      onCheckedChange={(checked) => {
                        setConnectedCalendars(prev =>
                          prev.map(c => 
                            c.provider === cal.provider 
                              ? { ...c, syncEnabled: checked }
                              : c
                          )
                        );
                      }}
                    />
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleDisconnect(cal.provider)}
                    >
                      <Unlink className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Sync Settings */}
          <div className="space-y-4 pt-4 border-t">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="bidirectional">{t.bidirectional}</Label>
                <p className="text-xs text-muted-foreground">{t.bidirectionalDesc}</p>
              </div>
              <Switch
                id="bidirectional"
                checked={syncSettings.bidirectional}
                onCheckedChange={(checked) => 
                  setSyncSettings(prev => ({ ...prev, bidirectional: checked }))
                }
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="notifications">{t.notifications}</Label>
                <p className="text-xs text-muted-foreground">{t.notificationsDesc}</p>
              </div>
              <Switch
                id="notifications"
                checked={syncSettings.notifications}
                onCheckedChange={(checked) => 
                  setSyncSettings(prev => ({ ...prev, notifications: checked }))
                }
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-4">
            <Button 
              className="flex-1" 
              onClick={handleSync}
              disabled={connectedCalendars.length === 0}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              {t.syncNow}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
